import axios from 'axios';
import { useStorage } from '@/composables/use-storage.js'
import { useToast } from '@/composables/use-toast.js';
import {
  disconnectWallet
} from '@/plugins/wagmi/connect';
import { useRouter } from 'vue-router';

const router = useRouter();
const { userAuthToken, userInfo, clearUserData } = useStorage();
const { addToast } = useToast();

const getAuthHeaders = () => {
  const token = userAuthToken.value;
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    withCredentials: true
  };
};


export const logApiError = async (error, method, url) => {
  if (error.response?.status === 401) {
    const hadSession = !!(localStorage.getItem('userInfos') || localStorage.getItem('authToken'));
    if (!hadSession) {
      console.error(`Error in API call:`, {
        message: error.message,
        stack: error.stack,
        method,
        url,
        status: error.response?.status,
        data: error.response?.data
      });
      return;
    }
    try {
      clearUserData();
      disconnectWallet();
      const response = await axios.post('/api/logout', {}, {
        headers: {
          'Authorization': `Bearer ${userAuthToken.value}`
        }
      });

      if (response.status === 200) {
        window.location.href = '/login';
        return;
      }
    } catch (logoutError) {
      console.error("Error during logout process:", logoutError);
      return;
    }
  } else if (error.response?.status === 503) {
    router.push('/status/503');
    return;
  }
  
  console.error(`Error in API call:`, {
    message: error.message,
    stack: error.stack,
    response: error.response?.data,
    status: error.response?.status,
    method,
    url,
    timestamp: new Date().toISOString()
  });
};


export const fetchDatasets = async (skip = null, take = null, type = null, includeLatestVersion = false, includeEvalFiles = false) => {
  try {
    let url = '/api/datasets';
    const params = [];
    
    if (skip !== null && take !== null) {
      params.push(`skip=${skip}&take=${take}`);
    }
    if (type !== null) {
      params.push(`type=${type}`);
    }
    if (includeLatestVersion) {
      params.push('include_latest_version=1');
    }
    if (includeEvalFiles) {
      params.push('include_eval_files=true');
    }
    if (params.length > 0) {
      url += '?' + params.join('&');
    }
    
    const response = await axios.get(url, getAuthHeaders());

    if (response.status === 200 || response.status === 201) {
      return response.data.sort((a, b) =>
        new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'GET', '/api/datasets');
    return null;
  }
};

export const fetchDatasetById = async (datasetId, version = null, leaderboard = false) => {
  try {
    let url = `/api/datasets/${datasetId}`;
    const params = [];
    if (version) {
      params.push(`version=${version}`);
    }
    if (leaderboard) {
      params.push('leaderboard=true');
    }
    if (params.length > 0) {
      url += '?' + params.join('&');
    }
    const response = await axios.get(url, getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      return response.data;
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'GET', `/api/datasets/${datasetId}`);
    return null;
  }
};

export const deleteDataset = async (datasetId, payload) => {
  try {
    const response = await axios.post(`/api/datasets/delete/${datasetId}`, payload, getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      const successMessage = response.data.message || response.message || 'Dataset deleted successfully!';
      addToast(successMessage, 'success');
      return response.data;
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    addToast(error.response.data.message, 'error');
    logApiError(error, 'DELETE', `/api/datasets/${datasetId}`);
    return false;
  }
};


export const fetchAllListings = async (skip = 0, take = 1000) => {
  try {
    const response = await axios.get(`/api/listings/all?skip=${skip}&take=${take}`, getAuthHeaders());
    return response.data.listings.sort((a, b) =>
      new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
  } catch (error) {
    logApiError(error, 'GET', '/api/listings/all');
    return [];
  }
};

export const fetchListings = async (skip = 0, take = 6, isEval = false) => {
  try{
    let url = `/api/listings?skip=${skip}&take=${take}`;
    if (isEval) {
      url += `&isEval=true`;
    }
    const response = await axios.get(url, getAuthHeaders());
    return response.data.listings.sort((a, b) =>
      new Date(b.created_at || 0) - new Date(a.created_at || 0)
  );
} catch (error) {
  logApiError(error, 'GET', `/api/listings?skip=${skip}&take=${take}`);
  return [];
}
};

export const fetchListingById = async (listingId) => {
  try {
    const response = await axios.get(`/api/listings/${listingId}`, getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      return response.data;
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'GET', `/api/listings/${listingId}`);
    return null;
  }
};

export const fetchTags = async () => {
  try {
    const response = await axios.get('/api/tags', getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      return response.data.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'GET', '/api/tags');
    return [];
  }
};

export const fetchSellers = async () => {
  try {
    const response = await axios.get('/api/sellers', getAuthHeaders());
    return response.data;
  } catch (error) {
    logApiError(error, 'GET', '/api/sellers');
    return [];
  }
};

export const fetchFiles = async (fileName) => {
  try {
    const response = await axios.get(`/api/files/${fileName}`, getAuthHeaders());
    return response;
  } catch (error) {
    logApiError(error, 'GET', `/api/files/${fileName}`);
    return false;
  }
}

export const fetchChatQuestionsAndAnswers = async (listingId) => {
  try {
    const response = await axios.get(`/api/chat/qa-data/${listingId}`, getAuthHeaders());
    return response.data;
  } catch (error) {
    logApiError(error, 'GET', `/api/chat/qa-data/${listingId}`);
    return [];
  }
}

export const sendChatQuestionToAPI = async (sessionId, listingId, question, chatHistory, model) => {
  try {
    const response = await axios.post(`/api/chat`, {
      session_id: sessionId,
      listing_id: listingId,
      msg: question,
      chat_history: chatHistory,
      model: model
    }, getAuthHeaders());
    return response.data;
  } catch (error) {
    logApiError(error, 'POST', `/api/qa-data/${listingId}/${question}`);
    return [];
  }
}

export const fetchChatHistory = async (sessionId) => {
  try {
    const response = await axios.get(`/api/chat/chat-history/${sessionId}`, getAuthHeaders());
    return response.data;
  } catch (error) {
    logApiError(error, 'GET', `/api/chat/chat-history/${sessionId}`);
    return [];
  }
}

export const createDataset = async (datasetData) => {
  try {
    const response = await axios.post('/api/datasets', datasetData, getAuthHeaders());

    if (response.status === 200 || response.status === 201) {
      const successMessage = response.data.message || response.message || 'Dataset created successfully!';
      addToast(successMessage, 'success');
      return response;
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'POST', '/api/datasets');
    const errorMessage = error.response?.data?.message ||
      error.message ||
      'Error creating dataset';
    addToast(errorMessage, 'error');
    return false;
  }
};


export const updateDataset = async (datasetData, datasetId = '') => {

  try {
    const response = await axios.post(`/api/datasets/${datasetId}`, datasetData, getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      const successMessage = 'Dataset updated successfully!';
      addToast(successMessage, 'success');
      return response;
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'POST', `/api/datasets/${datasetId}`);
    const errorMessage = error.response?.data?.message ||
      error.message ||
      'Error updating dataset';
    addToast(errorMessage, 'error');
    return false;
  }
};

export const updateDatasetOwner = async (datasetId, datasetData) => {
  try {
    const response = await axios.post(`/api/datasets/update_owner/${datasetId}`, datasetData, getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      const successMessage = 'Dataset owner updated successfully!';
      addToast(successMessage, 'success');
      return response;
    } else {
      throw new Error(response.data);
    }
  }
  catch (error) {
    logApiError(error, 'POST', `/api/datasets/update_owner/${datasetId}`);
    const errorMessage = error.response?.data?.message ||
      error.message ||
      'Error updating dataset owner';
    addToast(errorMessage, 'error');
    return false;
  }
}

export const createListing = async (listingData) => {
  try {
   
    const response = await axios.post('/api/listings', listingData, getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      const successMessage = response.message ? response.message : 'Listing created successfully!';
      addToast(successMessage, 'success');
      return response;
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'POST', '/api/listings');
    const errorMessage = error.response?.data?.message ||
      error.message ||
      'Error creating listing';
    addToast(errorMessage, 'error');
    return false;
  }
};


export const updateListing = async (listingId, listingData) => {
  try {
  
    const response = await axios.post(`/api/listings/${listingId}`, listingData, getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      const successMessage = response.message ? response.message : 'Listing updated successfully!';
      addToast(successMessage, 'success');
      return response;
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'POST', `/api/listings/${listingId}`);
    const errorMessage = error?.response?.data?.message ||
      error?.message ||
      'Error updating listing';
    addToast(errorMessage, 'error');
    return false;
  }
};

export const fetchUser = async () => {
  try {
    const userInfos = JSON.parse(localStorage.getItem('userInfos')) || JSON.parse(sessionStorage.getItem('userInfos'));
    if (userInfos) {
      const response = await axios.get(`/api/users/${userInfos.id}`, getAuthHeaders());
      if (response.data) {
        delete response.data.password;
        localStorage.setItem('userInfos', JSON.stringify(response.data));
        if (response.data.profile_pic && response.data.profile_pic.length > 0) {
          localStorage.setItem('profile_pic', response.data.profile_pic);
        }
        return response.data;
      }
    }

    return;
  } catch (error) {
    logApiError(error, 'GET', `/api/users/`);
    return;
  }
};

export const deleteListing = async (listingId, payload) => {
  try {
    const response = await axios.post(`/api/listings/delete/${listingId}`, payload, getAuthHeaders());
    return response;
  } catch (error) {
    if(error.status === 409) {
      addToast(error.response.data.message || 'Error deleting listing', 'error');
      return false;
    }
    logApiError(error, 'POST', `/api/listings/delete/${listingId}`);
    return false;
  }
};

export const fetchSellerById = async (sellerId) => {
  try {
    const response = await axios.get(`/api/sellers/${sellerId}`, getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      return response.data;
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'GET', `/api/sellers/${sellerId}`);
    return null;
  }
};

export const fetchExplore = async (deliveryMethod, skip, take) => {
  try{
    const response = await axios.get('/api/explore', {
      params: {
        delivery_method: deliveryMethod,
      skip,
      take
    }
  });
  return response.data;
} catch (error) {
  logApiError(error, 'GET', '/api/explore');
  return [];
}
};

export const createSeller = async (sellerData) => {
  try {
    const response = await axios.post('/api/sellers', sellerData, getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      const successMessage = response.message ? response.message : 'You are now registered as a seller!';
      addToast(successMessage, 'success');
      return response.data;
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'POST', '/api/sellers');
    const errorMessage = error.response?.data?.message ||
      error.message ||
      'Error registering as a seller';
    addToast(errorMessage, 'error');
    return null;
  }
};

const getUploadHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers = {
    'Accept': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const initiateUpload = async (baseURL, headers, fileName, fileSize, ownerId) => {
  const startResponse = await fetch(`${baseURL}/api/datasets/upload/start`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: fileName,
      size: fileSize,
      ownerId: Number(ownerId)
    })
  });

  if (!startResponse.ok) {
    const errorData = await startResponse.json();
    if (startResponse.status === 409) {
      addToast(errorData.message, 'error');
      return { error: 'FILE_EXISTS', message: errorData.message };
    }
    addToast(errorData.message, 'error');
    throw new Error('Failed to start upload');
  }

  const startResponseData = await startResponse.json();
  if (startResponseData.status !== 201) {
    return { error: startResponseData.message };
  }

  return startResponseData;
};

const parseFileName = (originalName) => {
  const parts = originalName.split('.');
  const fileName = parts[0];
  const fileExtension = parts.length > 2 ? `${parts[1]}.${parts[2]}` : parts[1];
  return { fileName, fileExtension };
};

const uploadChunk = async (baseURL, headers, chunk, uploadData) => {
  const formData = new FormData();
  formData.append('chunk', chunk);
  formData.append('uploadId', uploadData.uploadId);
  formData.append('chunkNumber', uploadData.chunkNumber.toString());
  formData.append('totalChunks', uploadData.totalChunks.toString());
  formData.append('filename', uploadData.fileName);
  formData.append('totalSize', uploadData.totalSize.toString());
  formData.append('fileType', uploadData.fileExtension);

  const chunkResponse = await fetch(`${baseURL}/api/datasets/upload/chunk`, {
    method: 'POST',
    credentials: 'include',
    headers: headers,
    body: formData
  });

  if (!chunkResponse.ok) {
    throw new Error(`Error sending chunk ${uploadData.chunkNumber}`);
  }
};


export const uploadDatasetFile = async (file, ownerId, onProgress, customName = null) => {
  const chunkMaxSize = 14;
  const chunkSize = chunkMaxSize * 1024 * 1024;
  const totalChunks = Math.ceil(file.size / chunkSize);
  const baseURL = axios.defaults.baseURL || 'http://localhost:3000';
  const headers = getUploadHeaders();
  const finalFileName = customName || file.name;

  try {
    const startResult = await initiateUpload(baseURL, headers, finalFileName, file.size, ownerId);
    if (startResult.error) return startResult;

    const { uploadId } = startResult;
    const { fileName, fileExtension } = parseFileName(finalFileName);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      await uploadChunk(baseURL, headers, chunk, {
        uploadId,
        chunkNumber: i + 1,
        totalChunks,
        fileName,
        totalSize: file.size,
        fileExtension
      });

      if (onProgress) {
        onProgress(((i + 1) / totalChunks) * 100);
      }
    }
    return startResult.status;
  } catch (error) {
    console.error('Error during upload:', error);
    throw error;
  }
};

export const deleteDatasetFile = async (fileName) => {
  try {
    const response = await axios.post(`/api/files/${fileName}/delete`, getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      const successMessage = response.data.message || response.message || 'File deleted successfully!';
      addToast(successMessage, 'success');
      return response.data;
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'DELETE', `/api/files/${fileName}/delete`);
    const errorMessage = error.response?.data?.message ||
      error.message ||
      'Error deleting dataset file';
    addToast(errorMessage, 'error');
    return false;
  }
};

export const fetchStorage = async () => {
  const response = await axios.get(`/api/sellers/${userInfo.value.id}/storage`, getAuthHeaders());
  return response.data;
};



export const revertDatasetVersion = async (datasetId, versionNumber) => {
  try {
    const response = await axios.post(`/api/datasets/revert`, 
      {dataset_id: datasetId, version: versionNumber }, 
      getAuthHeaders());
    if (response.status === 200 || response.status === 201) {
      return response.data;
    } else {
      throw new Error(response.data);
    }
  } catch (error) {
    logApiError(error, 'POST', `/api/datasets/revert`);
    const errorMessage = error.response?.data?.message ||
      error.message ||
      'Error reverting dataset version';
    addToast(errorMessage, 'error');
    return false;
  }
}

export { fetchDraftListing, createDraftListing, updateDraftListing, deleteDraftListing, fetchDraftByListingId } from '@/services/draftListingsService';
export { fetchMyProposals, fetchReviewProposals, fetchProposal, approveProposal, rejectProposal, withdrawProposal, hasProposalAccepted, createProposal, userHasProposalToListing } from '@/services/rfpProposalService';
export { fetchLicenses, fetchLicenseById, createLicense, updateLicense, deleteLicense, fetchLicensesOptions } from '@/services/licenseService';
export { uploadEvalFile, downloadEvalReport } from '@/services/evalService';
export { refundUSDC } from '@/services/walletService';